import { useEffect, useRef, useState, type FormEvent, type ReactElement, type Ref } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { CartaoControleEdicaoView, CartaoControleStatus } from '@pieltec/contracts';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { useToast } from '../../components/ui/toast';
import { CartaoControleA4 } from '../../components/producao/cartao-controle-a4';
import { cartaoControleApi, ErroCartaoControle, novaChaveIdempotencia } from '../../lib/rastreamento/cartao-controle-api';
import { edicaoVisualizada, fatosCabecalhoCp, ordenarHistoricoCp } from '../../lib/rastreamento/edicao-cp';
import { obterSessaoAtual } from '../../lib/session';

export type EstadoCartaoControle = 'carregando' | 'erro' | 'vazio' | 'parcial' | 'pronto';
export function estadoCartaoControle(input: { readonly carregando: boolean; readonly erro: string | null; readonly possuiEdicao: boolean; readonly parcial: boolean }): EstadoCartaoControle {
  if (input.carregando) return 'carregando';
  if (input.erro) return 'erro';
  if (!input.possuiEdicao) return 'vazio';
  return input.parcial ? 'parcial' : 'pronto';
}

/** Critérios nominais do UI-SPEC mantidos junto à tela para evitar regressão silenciosa. */
export const criteriosUiCpQr: Readonly<Record<string, string>> = {
  'UI-01': 'CP vazio permite preparar a primeira edição.',
  'UI-02': 'CP em leitura mantém contexto e envio ocupa a ação uma vez.',
  'UI-03': 'Erro de CP preserva contexto e reconcilia antes de repetir.',
  'UI-04': 'CP preparado mostra identificação, situação, revisão, data e QR.',
  'UI-05': 'CP parcial rotula ausências sem converter desconhecido em zero.',
  'UI-06': 'CP A4 pagina roteiro extenso em continuações identificadas.',
  'UI-07': 'CP trata zero, um e muitos com ordem estável.',
  'UI-08': 'Texto longo quebra e fica íntegro por toque/teclado.',
  'UI-09': 'QR vazio indica ausência sem revelar metadados inexistentes.',
  'UI-10': 'QR em leitura preserva contexto e evita envio duplicado.',
  'UI-11': 'Erro de QR preserva contexto e orienta reconciliação.',
  'UI-12': 'Resumo QR mostra lote, OP, revisão, processos, insumos e genealogia.',
  'UI-13': 'QR parcial rotula ausências sem fatos fictícios.',
  'UI-14': 'QR extenso usa detalhe responsivo sem rolagem horizontal.',
  'UI-15': 'QR trata zero, um e muitos com ordem estável.',
  'UI-16': 'Textos do QR permanecem acessíveis por toque/teclado, sem hover.',
};

export const TEXTO_ERRO_LEITURA = 'Não foi possível carregar os dados. Verifique a conexão e tente novamente.';
export const TEXTO_CONFLITO = 'Este registro foi alterado por outra pessoa. Recarregue para conferir a situação atual.';
export const TEXTO_RESULTADO_INCERTO = 'Não foi possível confirmar o resultado desta ação. Consulte a situação atual antes de tentar novamente.';
export const TEXTO_MOTIVO_OBRIGATORIO = 'Informe o motivo da reimpressão.';
export const ROTULO_REIMPRIMIR = 'Reimprimir cartão de controle';
const TEXTO_REIMPRESSAO_RECUSADA = 'Não foi possível reimprimir o cartão de controle. Revise o motivo e tente novamente.';

export const ID_MOTIVO_REIMPRESSAO = 'cp-motivo-reimpressao';
export const ID_ERRO_MOTIVO_REIMPRESSAO = 'cp-motivo-erro';

/** Validação local do motivo: o envio só sai com texto não vazio (o servidor continua exigindo o motivo). */
export function validarMotivoReimpressao(motivo: string): string | null {
  return motivo.trim() ? null : TEXTO_MOTIVO_OBRIGATORIO;
}

export type FalhaReimpressao = 'conflito' | 'incerto' | 'definitiva';

/**
 * Resultado incerto (rede, 5xx, sem status): a chave de idempotência da tentativa é reutilizada.
 * 409 e demais 4xx são respostas definitivas: a chave é descartada.
 */
export function classificarFalhaReimpressao(causa: unknown): FalhaReimpressao {
  if (!(causa instanceof ErroCartaoControle)) return 'incerto';
  if (causa.status === 409) return 'conflito';
  return causa.status >= 400 && causa.status < 500 ? 'definitiva' : 'incerto';
}

export function textoDaFalhaReimpressao(tipo: FalhaReimpressao): string {
  if (tipo === 'conflito') return TEXTO_CONFLITO;
  return tipo === 'incerto' ? TEXTO_RESULTADO_INCERTO : TEXTO_REIMPRESSAO_RECUSADA;
}

export function CartaoControleCarregando(): ReactElement {
  return <main className="rastreamento-page" aria-busy="true">
    <h1>Cartão de controle</h1>
    <div className="ui-skeleton cp-skeleton-cabecalho" data-slot="skeleton-cabecalho" />
    <div className="ui-skeleton cp-skeleton-corpo" data-slot="skeleton-corpo" />
    <p className="sr-only" role="status">Carregando cartão de controle…</p>
  </main>;
}

export function CartaoControleErroLeitura({ mensagem = TEXTO_ERRO_LEITURA, onTentarNovamente }: { readonly mensagem?: string; readonly onTentarNovamente?: () => void }): ReactElement {
  return <main className="rastreamento-page">
    <h1>Cartão de controle</h1>
    <div className="cp-alerta cp-alerta--erro" role="alert">
      <p><AlertTriangle size={16} aria-hidden="true" /> {mensagem}</p>
      {onTentarNovamente && <button type="button" onClick={onTentarNovamente}>Tentar novamente</button>}
    </div>
  </main>;
}

export interface HistoricoEdicoesCpProps {
  readonly historico: readonly CartaoControleEdicaoView[];
  readonly selecionadaId: string | null;
  readonly onSelecionar?: (edicaoId: string) => void;
}

/** Todas as edições em ordem estável; nenhuma é apagada, só marcada como substituída. */
export function HistoricoEdicoesCp({ historico, selecionadaId, onSelecionar }: HistoricoEdicoesCpProps): ReactElement {
  const ordenado = ordenarHistoricoCp(historico);
  return <section className="cp-historico cp-nao-imprime" aria-labelledby="cp-historico-titulo">
    <h2 id="cp-historico-titulo">Histórico de edições</h2>
    {ordenado.length === 0 ? <p>Nenhuma edição registrada.</p> : <ol>
      {ordenado.map((edicao) => {
        const fatos = fatosCabecalhoCp(edicao, historico);
        const selecionada = edicao.id === selecionadaId;
        return <li key={edicao.id} data-selecionada={selecionada}>
          <button type="button" className="cp-historico__selecionar" aria-pressed={selecionada} onClick={() => onSelecionar?.(edicao.id)}>
            <span>{fatos.identificador}</span>
            <span className="cp-historico__data">{fatos.dataHora}</span>
            <span>{fatos.situacao}</span>
          </button>
          <p>{edicao.motivo?.trim() ? edicao.motivo : 'Sem motivo informado'}</p>
          {fatos.substituidaPor !== null
            ? <p className="cp-historico__substituicao">Substituído por edição {fatos.substituidaPor}</p>
            : <p>{edicao.estado === 'DESATUALIZADA' ? 'Desatualizada' : 'Atual'}</p>}
        </li>;
      })}
    </ol>}
  </section>;
}

export function AcoesCartaoControle({ podeReimprimir, onReimprimir }: { readonly podeReimprimir: boolean; readonly onReimprimir: () => void }): ReactElement | null {
  if (!podeReimprimir) return null;
  return <div className="cp-acoes cp-nao-imprime"><button type="button" className="cp-botao--secundario" onClick={onReimprimir}>{ROTULO_REIMPRIMIR}</button></div>;
}

export interface ReimpressaoCpCorpoProps {
  readonly motivo: string;
  readonly erroCampo: string | null;
  readonly erroEnvio: { readonly tipo: FalhaReimpressao; readonly texto: string } | null;
  readonly enviando: boolean;
  readonly onMotivoChange?: (motivo: string) => void;
  readonly onRecarregar?: () => void;
  /** Recebe o foco quando o motivo é recusado pela validação local. */
  readonly motivoRef?: Ref<HTMLTextAreaElement>;
}

export function ReimpressaoCpCorpo({ motivo, erroCampo, erroEnvio, enviando, onMotivoChange, onRecarregar, motivoRef }: ReimpressaoCpCorpoProps): ReactElement {
  return <div className="cp-reimpressao">
    <label className="cp-campo" htmlFor={ID_MOTIVO_REIMPRESSAO}>Motivo da reimpressão
      <textarea ref={motivoRef} id={ID_MOTIVO_REIMPRESSAO} value={motivo} disabled={enviando} required aria-required="true" aria-invalid={erroCampo !== null} aria-describedby={erroCampo ? ID_ERRO_MOTIVO_REIMPRESSAO : undefined} onChange={(event) => onMotivoChange?.(event.target.value)} />
    </label>
    {erroCampo && <p id={ID_ERRO_MOTIVO_REIMPRESSAO} className="cp-campo__erro" role="alert">{erroCampo}</p>}
    {erroEnvio && <div className="cp-alerta cp-alerta--erro" role="alert">
      <p>{erroEnvio.texto}</p>
      {erroEnvio.tipo !== 'definitiva' && <button type="button" className="cp-botao cp-botao--secundario" onClick={onRecarregar}>Recarregar situação atual</button>}
    </div>}
  </div>;
}

export function CartaoControlePage(): ReactElement {
  const loteId = new URLSearchParams(window.location.search).get('loteId')?.trim() ?? '';
  const opId = new URLSearchParams(window.location.search).get('opId')?.trim() ?? '';
  const toast = useToast();
  const [status, setStatus] = useState<CartaoControleStatus | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [selecionadaId, setSelecionadaId] = useState<string | null>(null);
  const [podeReimprimir, setPodeReimprimir] = useState(false);
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erroCampo, setErroCampo] = useState<string | null>(null);
  const [erroEnvio, setErroEnvio] = useState<{ readonly tipo: FalhaReimpressao; readonly texto: string } | null>(null);
  // Uma chave por tentativa: só é descartada após sucesso ou resposta 4xx definitiva (T-16-12-T).
  const chaveReimpressao = useRef<string | null>(null);
  const motivoRef = useRef<HTMLTextAreaElement>(null);

  const carregar = async (silencioso = false): Promise<void> => {
    if (!silencioso) setCarregando(true);
    setErro(null);
    try {
      if (!loteId) throw new Error('Lote não informado.');
      setStatus(await cartaoControleApi.status(loteId));
    } catch (cause) {
      setErro(!loteId && cause instanceof Error ? cause.message : TEXTO_ERRO_LEITURA);
    } finally { setCarregando(false); }
  };
  useEffect(() => { void carregar(); }, [loteId]);
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo) setPodeReimprimir(sessao.autenticado && sessao.usuario.permissions.includes('producao:gerenciar'));
    });
    return () => { vivo = false; };
  }, []);

  const preparar = async (): Promise<void> => { if (!loteId || !opId || enviando) return; setEnviando(true); setErro(null); try { await cartaoControleApi.preparar(loteId, opId); await carregar(); toast.success({ title: 'Edição do cartão preparada para impressão.' }); } catch (cause) { setErro(cause instanceof Error ? cause.message : 'O resultado é incerto. Recarregue a situação atual antes de repetir.'); } finally { setEnviando(false); } };

  const edicaoAtual = status?.edicaoAtual ?? null;
  const historico = status?.historico ?? [];

  const abrirReimpressao = (): void => { setErroCampo(null); setErroEnvio(null); setDialogoAberto(true); };
  const reimprimir = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (enviando) return;
    // Motivo vazio nunca é ignorado em silêncio: mensagem no campo, aria-invalid e foco no motivo.
    const erroMotivo = validarMotivoReimpressao(motivo);
    if (erroMotivo) { setErroCampo(erroMotivo); motivoRef.current?.focus(); return; }
    setErroCampo(null);
    if (!edicaoAtual) { setErroEnvio({ tipo: 'conflito', texto: TEXTO_CONFLITO }); return; }
    setErroEnvio(null);
    setEnviando(true);
    const chave = chaveReimpressao.current ?? novaChaveIdempotencia();
    chaveReimpressao.current = chave;
    try {
      await cartaoControleApi.reimprimir(edicaoAtual.id, motivo.trim(), chave);
      chaveReimpressao.current = null;
      setMotivo('');
      setDialogoAberto(false);
      setSelecionadaId(null);
      await carregar(true);
      toast.success({ title: 'Edição do cartão preparada para impressão.' });
    } catch (causa) {
      const tipo = classificarFalhaReimpressao(causa);
      if (tipo !== 'incerto') chaveReimpressao.current = null;
      setErroEnvio({ tipo, texto: textoDaFalhaReimpressao(tipo) });
    } finally { setEnviando(false); }
  };

  const edicao = edicaoVisualizada(historico, selecionadaId, edicaoAtual);
  const parcial = edicao?.snapshot.op.quantidade.estado === 'NAO_INFORMADA' || edicao?.snapshot.roteiro.estado === 'AUSENTE';
  const estado = estadoCartaoControle({ carregando, erro, possuiEdicao: edicao !== null, parcial: parcial ?? false });
  if (estado === 'carregando') return <CartaoControleCarregando />;
  if (estado === 'erro') return <CartaoControleErroLeitura mensagem={erro ?? TEXTO_ERRO_LEITURA} onTentarNovamente={() => void carregar()} />;
  if (estado === 'vazio') return <main className="rastreamento-page"><h1>Nenhum cartão de controle impresso para este lote.</h1><p>Imprima a primeira edição quando precisar acompanhar o lote fisicamente.</p><button type="button" disabled={!opId || enviando} onClick={() => void preparar()}>{enviando ? 'Preparando edição…' : 'Preparar primeira edição'}</button></main>;
  return <main className="rastreamento-page" data-density="touch">
    {estado === 'parcial' && <p className="rastreamento-alerta cp-nao-imprime">Alguns dados do cartão não foram informados. Nenhuma quantidade foi inventada.</p>}
    <AcoesCartaoControle podeReimprimir={podeReimprimir} onReimprimir={abrirReimpressao} />
    <HistoricoEdicoesCp historico={historico} selecionadaId={edicao!.id} onSelecionar={setSelecionadaId} />
    <CartaoControleA4 edicao={edicao!} historico={historico} aoPrepararImpressao={async (id) => { await cartaoControleApi.registrarAberturaFluxoImpressao(id); toast.info({ title: 'Edição do cartão preparada para impressão.' }); }} />
    <FormDialog open={dialogoAberto} onOpenChange={(aberto) => { if (!enviando) setDialogoAberto(aberto); }}>
      <FormDialogContent>
        <form onSubmit={(event) => void reimprimir(event)} noValidate>
          <FormDialogHeader>
            <FormDialogTitle>{ROTULO_REIMPRIMIR}</FormDialogTitle>
            <FormDialogDescription>Cria uma nova edição ligada à anterior. As edições anteriores continuam no histórico.</FormDialogDescription>
          </FormDialogHeader>
          <FormDialogBody>
            <ReimpressaoCpCorpo motivo={motivo} erroCampo={erroCampo} erroEnvio={erroEnvio} enviando={enviando} motivoRef={motivoRef} onMotivoChange={(valor) => { setMotivo(valor); if (erroCampo && valor.trim()) setErroCampo(null); }} onRecarregar={() => void carregar(true)} />
          </FormDialogBody>
          <FormDialogFooter>
            <button type="button" className="cp-botao cp-botao--secundario" disabled={enviando} onClick={() => setDialogoAberto(false)}>Cancelar</button>
            <button type="submit" className="cp-botao" disabled={enviando}>{enviando ? 'Reimprimindo…' : ROTULO_REIMPRIMIR}</button>
          </FormDialogFooter>
        </form>
      </FormDialogContent>
    </FormDialog>
  </main>;
}
